import type { AppConfig } from "../../types/appConfig";
import { assertResponseOk, expectStatuses, fetchZekay } from "../zekay/httpClient";
import type { ApiPermissionKey } from "../zekay/permissions";
import type { PermissionsCatalog, Role } from "./Role.typed";

function parseRoleWire(raw: unknown): Role {
  if (raw == null || typeof raw !== "object") {
    return { id: 0, name: "", permission_list: [], is_employee: false };
  }
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  const name = typeof r.name === "string" ? r.name : "";
  const pl = Array.isArray(r.permission_list)
    ? r.permission_list
    : Array.isArray(r.permissionList)
      ? r.permissionList
      : [];
  const permission_list = pl.filter(
    (x): x is string => typeof x === "string",
  ) as ApiPermissionKey[];
  const is_employee =
    typeof r.is_employee === "boolean"
      ? r.is_employee
      : typeof r.isEmployee === "boolean"
        ? r.isEmployee
        : false;
  const cid = r.company_id;
  const company_id = cid != null && Number.isFinite(Number(cid)) ? Number(cid) : undefined;
  const out: Role = {
    id: Number.isFinite(id) ? id : 0,
    name,
    permission_list,
    is_employee,
  };
  if (company_id !== undefined) out.company_id = company_id;
  return out;
}

export class RoleModel {
  zekayAPI: string;

  constructor(config: AppConfig) {
    this.zekayAPI = config.zekayAPI;
  }

  async getPermissionsCatalog(): Promise<PermissionsCatalog> {
    const res = await fetchZekay(this.zekayAPI + "/roles/permissions");
    await assertResponseOk(res);
    const json = await res.json();
    const data = json as Record<string, unknown>;
    const rolesRaw = Array.isArray(data.roles) ? data.roles : [];
    const roles = rolesRaw.map((row) => parseRoleWire(row)).filter((role) => role.id > 0);
    const allRaw = data.allPermissions ?? data.all_permissions;
    const allPermissions = Array.isArray(allRaw) ? (allRaw as ApiPermissionKey[]) : [];
    return { roles, allPermissions };
  }

  async getRole(roleId: number): Promise<Role> {
    const res = await fetchZekay(this.zekayAPI + `/roles/${roleId}`);
    await assertResponseOk(res);
    const json = await res.json();
    return parseRoleWire(json);
  }

  async createRole(payload: {
    name: string;
    permission_list: string[];
    company_id: number;
    is_employee?: boolean;
  }): Promise<void> {
    const res = await fetchZekay(this.zekayAPI + "/roles", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: payload.name,
        permission_list: payload.permission_list,
        company_id: payload.company_id,
        ...(payload.is_employee !== undefined ? { is_employee: payload.is_employee } : {}),
      }),
    });
    await expectStatuses(res, 201);
  }

  async updateRole(
    roleId: number,
    payload: Partial<{ name: string; permission_list: string[]; is_employee: boolean }>,
  ): Promise<void> {
    const body: Record<string, unknown> = {};
    if (payload.name !== undefined) body.name = payload.name;
    if (payload.permission_list !== undefined) body.permission_list = payload.permission_list;
    if (payload.is_employee !== undefined) body.is_employee = payload.is_employee;
    const res = await fetchZekay(this.zekayAPI + `/roles/${roleId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    await expectStatuses(res, 204);
  }

  async deleteRole(roleId: number): Promise<void> {
    const res = await fetchZekay(this.zekayAPI + `/roles/${roleId}`, {
      method: "DELETE",
    });
    await expectStatuses(res, 204);
  }
}
